import { setFullStoryUser } from "~/lib/fullStory.client"
import { identifyHubSpotUser } from "~/lib/hubspot.client"
import { setMixpanelUser } from "~/lib/mixpanel.client"
import { setSentryUser } from "~/lib/sentry.client"

/**
 * Shape of the current user as consumed by analytics identity. Kept minimal on
 * purpose — anything that can produce these fields can drive identity.
 */
export interface IdentityUser {
  id: string | number
  firstName?: string
  lastName?: string
  email: string
}

/**
 * Module-level dedup shared by EVERY identity entry point (the useUser-driven
 * `useIdentitySync` hook and the query-cache subscriber in queryClient.ts).
 * Whichever path fires first wins; the others become no-ops. This is what makes
 * running two redundant mechanisms safe — they can never double-identify or
 * fight each other.
 */
let lastIdentifiedUserId: string | null = null

/**
 * Single source of truth for pushing user identity into FullStory, Mixpanel and
 * Sentry. Pass a user to identify, `null`/`undefined` to clear (logout /
 * anonymous). Idempotent per user id. Reachable from both render state and
 * cache events so identity is set exactly once however the user becomes known.
 */
export const syncIdentity = (user: IdentityUser | null | undefined): void => {
  if (typeof window === "undefined") return

  if (user) {
    // FullStory/Sentry expect string uids; user.id may be numeric.
    const id = String(user.id)
    if (id === lastIdentifiedUserId) return

    const displayName = [user.firstName, user.lastName]
      .filter(Boolean)
      .join(" ")

    // eslint-disable-next-line no-console
    console.log("[Identity] Setting user identity:", {
      id,
      displayName,
      email: user.email,
    })

    setFullStoryUser(id, displayName, user.email)
    setMixpanelUser(id, displayName, user.email)
    setSentryUser(id, user.email, displayName || undefined)
    identifyHubSpotUser(user.email)
    lastIdentifiedUserId = id
  } else {
    if (lastIdentifiedUserId === null) return

    // eslint-disable-next-line no-console
    console.log("[Identity] Clearing user identity (logout/anonymous)")

    setFullStoryUser(null, "", "")
    setMixpanelUser(null, "", "")
    setSentryUser(null)
    lastIdentifiedUserId = null
  }
}
